// components/landing/LandingNavBar.tsx
// ---------------------------------------------------------------------------
// Public-facing navbar for the landing page.
// Accepts callbacks for Sign In (shows login in hero card) and anchor
// navigation (handled by LandingPage to coordinate scroll-snap).
// ---------------------------------------------------------------------------

import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Menu as MenuIcon,
} from "@mui/icons-material";
import {
  AppBar,
  Box,
  Button,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Popper,
  Toolbar,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { athenaColors } from "./colors";

interface NavLink {
  label: string;
  href: string;
}

const NAV_LINKS: NavLink[] = [
  { label: "Product", href: "#product" },
  { label: "Solutions", href: "__solutions__" },
  // { label: "Pricing", href: "#pricing" }, // hidden per request
  { label: "Team", href: "/team" },
  { label: "Contact", href: "/contact" },
];

interface SolutionItem {
  logo: string;
  alt: string;
  description: string;
  href: string;
  external: boolean;
  active: boolean;
}

const SOLUTIONS: SolutionItem[] = [
  {
    logo: "/brand/inline/athena_rwe_white_path.svg",
    alt: "Athena RWE",
    description: "Agentic system for multi-cancer clinical data structuring",
    href: "__signin__",
    external: false,
    active: true,
  },
  {
    logo: "/brand/inline/athena_xxmm_white_path.svg",
    alt: "Athena XXMM",
    description: "Melanoma clinical registry for Xarxa de Melanoma",
    href: "https://xxmm.athenatechai.com",
    external: true,
    active: false,
  },
];

interface LandingNavBarProps {
  onSignIn: () => void;
  onNavClick: (href: string) => void;
}

export default function LandingNavBar({
  onSignIn,
  onNavClick,
}: LandingNavBarProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Solutions dropdown state
  const solutionsAnchorRef = useRef<HTMLButtonElement | null>(null);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Mobile: expandable solutions section
  const [mobileSolutionsOpen, setMobileSolutionsOpen] = useState(false);

  const openSolutions = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setSolutionsOpen(true);
  };
  const scheduleSolutionsClose = () => {
    closeTimer.current = setTimeout(() => setSolutionsOpen(false), 200);
  };

  const handleNavClick = (href: string) => {
    setDrawerOpen(false);
    setSolutionsOpen(false);
    if (href === "__solutions__") return;
    if (href.startsWith("/")) {
      navigate(href);
      return;
    }
    onNavClick(href);
  };

  const handleSolutionClick = (item: SolutionItem) => {
    setSolutionsOpen(false);
    setDrawerOpen(false);
    if (item.external) {
      window.open(item.href, "_blank", "noopener,noreferrer");
    } else if (item.href === "__signin__") {
      onSignIn();
    } else {
      onNavClick(item.href);
    }
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          height: theme.custom.navbarHeight,
          background: `linear-gradient(
            180deg,
            ${alpha(theme.palette.primary.main, 0.92)} 0%,
            ${alpha(theme.palette.primary.main, 0.85)} 100%
          )`,
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          borderBottom: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
          boxShadow: `0 4px 20px ${alpha(theme.palette.primary.dark, 0.2)}`,
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            minHeight: theme.custom.navbarHeight,
            height: theme.custom.navbarHeight,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: { xs: 2, md: 4 },
          }}
        >
          {/* LEFT: Logo */}
          <Box
            sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            <Box
              component="img"
              src="/brand/inline/athena_tech_white_path.svg"
              alt="Athena Tech"
              sx={{
                height: theme.custom.navbarHeight * 0.45,
                width: "auto",
              }}
            />
          </Box>

          {/* CENTER: Nav links (desktop) */}
          {!isMobile && (
            <Box sx={{ display: "flex", gap: 1 }}>
              {NAV_LINKS.map((link) =>
                link.href === "__solutions__" ? (
                  <Box
                    key="solutions"
                    onMouseEnter={openSolutions}
                    onMouseLeave={scheduleSolutionsClose}
                  >
                    <Button
                      ref={solutionsAnchorRef}
                      onClick={() => setSolutionsOpen((o) => !o)}
                      endIcon={
                        <ExpandMoreIcon
                          sx={{
                            fontSize: "1rem !important",
                            transition: "transform 0.2s",
                            transform: solutionsOpen ? "rotate(180deg)" : "none",
                          }}
                        />
                      }
                      sx={{
                        color: solutionsOpen
                          ? theme.palette.secondary.main
                          : alpha(theme.palette.secondary.main, 0.85),
                        fontWeight: 500,
                        fontSize: "0.9rem",
                        textTransform: "none",
                        borderRadius: 2,
                        px: 1.5,
                        "&:hover": {
                          color: theme.palette.secondary.main,
                          background: alpha(theme.palette.secondary.main, 0.08),
                        },
                      }}
                    >
                      {link.label}
                    </Button>
                    <Popper
                      open={solutionsOpen}
                      anchorEl={solutionsAnchorRef.current}
                      placement="bottom-start"
                      disablePortal
                      sx={{ zIndex: theme.zIndex.appBar + 1 }}
                    >
                      <Paper
                        onMouseEnter={openSolutions}
                        onMouseLeave={scheduleSolutionsClose}
                        elevation={8}
                        sx={{
                          mt: 0.5,
                          p: 1,
                          minWidth: 320,
                          borderRadius: 2,
                          background: alpha(athenaColors.deepNavy, 0.95),
                          backdropFilter: "blur(16px)",
                          border: `1px solid ${alpha(theme.palette.secondary.main, 0.15)}`,
                        }}
                      >
                        {SOLUTIONS.map((item) => (
                          <Box
                            key={item.alt}
                            onClick={() => handleSolutionClick(item)}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              px: 1.5,
                              py: 1.25,
                              borderRadius: 1.5,
                              cursor: "pointer",
                              borderLeft: item.active
                                ? `3px solid ${athenaColors.royalBlue}`
                                : "3px solid transparent",
                              "&:hover": {
                                bgcolor: alpha(theme.palette.secondary.main, 0.08),
                              },
                            }}
                          >
                            <Box
                              component="img"
                              src={item.logo}
                              alt={item.alt}
                              sx={{ height: 22, width: "auto", flexShrink: 0 }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                color: alpha(theme.palette.secondary.main, 0.7),
                                lineHeight: 1.4,
                                fontSize: "0.75rem",
                              }}
                            >
                              {item.description}
                            </Typography>
                          </Box>
                        ))}
                      </Paper>
                    </Popper>
                  </Box>
                ) : (
                  <Button
                    key={link.href}
                    onClick={() => handleNavClick(link.href)}
                    sx={{
                      color: alpha(theme.palette.secondary.main, 0.85),
                      fontWeight: 500,
                      fontSize: "0.9rem",
                      textTransform: "none",
                      borderRadius: 2,
                      px: 1.5,
                      "&:hover": {
                        color: theme.palette.secondary.main,
                        background: alpha(theme.palette.secondary.main, 0.08),
                      },
                    }}
                  >
                    {link.label}
                  </Button>
                )
              )}
            </Box>
          )}

          {/* RIGHT: Sign In CTA */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {!isMobile && (
              <Button
                onClick={onSignIn}
                sx={{
                  color: theme.palette.secondary.main,
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": {
                    background: alpha(theme.palette.secondary.main, 0.1),
                  },
                }}
              >
                Sign In
              </Button>
            )}
            {!isMobile && (
              <Button
                variant="contained"
                color="secondary"
                // Pricing section is hidden; route the primary CTA to sign-in
                // instead of scrolling to the now-absent #pricing anchor.
                onClick={onSignIn}
                sx={{
                  fontWeight: 700,
                  textTransform: "none",
                  borderRadius: 3,
                  px: 2.5,
                  background: `linear-gradient(135deg, ${athenaColors.orange} 0%, ${athenaColors.lightOrange} 100%)`,
                  color: theme.palette.common.white,
                  border: "none",
                  "&:hover": {
                    background: `linear-gradient(135deg, ${athenaColors.lightOrange} 0%, ${athenaColors.orange} 100%)`,
                    transform: "translateY(-1px)",
                  },
                }}
              >
                Begin the Journey
              </Button>
            )}

            {/* Mobile hamburger */}
            {isMobile && (
              <IconButton
                onClick={() => setDrawerOpen(true)}
                sx={{ color: theme.palette.secondary.main }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 280,
            background: alpha(theme.palette.primary.main, 0.97),
            backdropFilter: "blur(16px)",
            color: theme.palette.secondary.main,
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
          <IconButton
            onClick={() => setDrawerOpen(false)}
            sx={{ color: "inherit" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <List>
          {NAV_LINKS.map((link) =>
            link.href === "__solutions__" ? (
              <Box key="solutions-mobile">
                <ListItem
                  onClick={() => setMobileSolutionsOpen((o) => !o)}
                  sx={{
                    cursor: "pointer",
                    "&:hover": { bgcolor: alpha(theme.palette.common.white, 0.05) },
                  }}
                >
                  <ListItemText
                    primary="Solutions"
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                  <ExpandMoreIcon
                    sx={{
                      fontSize: "1.2rem",
                      transition: "transform 0.2s",
                      transform: mobileSolutionsOpen ? "rotate(180deg)" : "none",
                    }}
                  />
                </ListItem>
                <Collapse in={mobileSolutionsOpen}>
                  <Box sx={{ pl: 2, pb: 1 }}>
                    {SOLUTIONS.map((item) => (
                      <ListItem
                        key={item.alt}
                        onClick={() => handleSolutionClick(item)}
                        sx={{
                          cursor: "pointer",
                          borderRadius: 1,
                          "&:hover": { bgcolor: alpha(theme.palette.common.white, 0.05) },
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Box
                            component="img"
                            src={item.logo}
                            alt={item.alt}
                            sx={{ height: 18, width: "auto" }}
                          />
                          <Typography
                            variant="caption"
                            sx={{ color: alpha(theme.palette.common.white, 0.65), fontSize: "0.72rem" }}
                          >
                            {item.description}
                          </Typography>
                        </Box>
                      </ListItem>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            ) : (
              <ListItem
                key={link.href}
                onClick={() => handleNavClick(link.href)}
                sx={{
                  cursor: "pointer",
                  "&:hover": { bgcolor: alpha(theme.palette.common.white, 0.05) },
                }}
              >
                <ListItemText
                  primary={link.label}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
            )
          )}
          <Divider sx={{ borderColor: alpha(theme.palette.common.white, 0.15), my: 1 }} />
          <ListItem
            onClick={() => {
              setDrawerOpen(false);
              onSignIn();
            }}
            sx={{
              cursor: "pointer",
              "&:hover": { bgcolor: alpha(theme.palette.common.white, 0.05) },
            }}
          >
            <ListItemText
              primary="Sign In"
              primaryTypographyProps={{ fontWeight: 700 }}
            />
          </ListItem>
          <ListItem
            onClick={() => {
              setDrawerOpen(false);
              handleNavClick("#pricing");
            }}
            sx={{
              cursor: "pointer",
              mt: 1,
              mx: 2,
              borderRadius: 2,
              justifyContent: "center",
              width: "auto",
              background: `linear-gradient(135deg, ${athenaColors.orange} 0%, ${athenaColors.lightOrange} 100%)`,
            }}
          >
            <ListItemText
              primary="Begin the Journey"
              primaryTypographyProps={{
                fontWeight: 700,
                align: "center",
                sx: { color: theme.palette.common.white },
              }}
            />
          </ListItem>
        </List>
      </Drawer>
    </>
  );
}
